import { createGaussianData } from "./helpers/GaussianData";
import { createUniformSampler } from "./helpers/GaussianSampling";
import { halfToFloat } from "./helpers/BinaryUtils";
import { writeNormalizedQuaternionWXYZ } from "./helpers/QuaternionUtils";
import { debugLog } from "../../../utils/debug";

const HEADER_SIZE = 4096;
const SECTION_HEADER_SIZE = 1024;

const COMPRESSION = {
  0: {
    centerBytes: 12,
    scaleBytes: 12,
    rotationBytes: 16,
    colorBytes: 4,
    shBytes: 4,
    defaultScaleRange: 1,
  },
  1: {
    centerBytes: 6,
    scaleBytes: 6,
    rotationBytes: 8,
    colorBytes: 4,
    shBytes: 2,
    defaultScaleRange: 32767,
  },
  2: {
    centerBytes: 6,
    scaleBytes: 6,
    rotationBytes: 8,
    colorBytes: 4,
    shBytes: 1,
    defaultScaleRange: 32767,
  },
};

const SH_COMPONENTS = {
  0: 0,
  1: 9,
  2: 24,
  3: 45,
};

/**
 * Converts KSPLAT coordinates into the Three.js scene convention:
 * (x, y, z) -> (x, -z, y).
 */
function writeConvertedPosition(positions, outputIndex, x, y, z) {
  const offset = outputIndex * 3;

  positions[offset] = x;
  positions[offset + 1] = -z;
  positions[offset + 2] = y;
}

/**
 * Decodes one KSPLAT record into the normalized Gaussian arrays.
 */
function decodeGaussian(
  view,
  section,
  compressionLevel,
  bucketIndices,
  sourceIndex,
  outputIndex,
  positions,
  scales,
  rotations,
  colors,
  opacities,
) {
  let offset = section.splatDataOffset + sourceIndex * section.bytesPerSplat;

  if (compressionLevel === 0) {
    const x = view.getFloat32(offset, true);
    const y = view.getFloat32(offset + 4, true);
    const z = view.getFloat32(offset + 8, true);

    writeConvertedPosition(positions, outputIndex, x, y, z);

    offset += 12;
  } else {
    const bucketIndex = bucketIndices[sourceIndex];

    const bucketOffset = section.bucketOffset + bucketIndex * 12;

    const bucketX = view.getFloat32(bucketOffset, true);

    const bucketY = view.getFloat32(bucketOffset + 4, true);

    const bucketZ = view.getFloat32(bucketOffset + 8, true);

    const conversion = section.blockSize / (2 * section.scaleRange);

    const compressedX = view.getUint16(offset, true);

    const compressedY = view.getUint16(offset + 2, true);

    const compressedZ = view.getUint16(offset + 4, true);

    const x = (compressedX - section.scaleRange) * conversion + bucketX;

    const y = (compressedY - section.scaleRange) * conversion + bucketY;

    const z = (compressedZ - section.scaleRange) * conversion + bucketZ;

    writeConvertedPosition(positions, outputIndex, x, y, z);

    offset += 6;
  }

  const vectorOffset = outputIndex * 3;

  if (compressionLevel === 0) {
    scales[vectorOffset] = view.getFloat32(offset, true);

    scales[vectorOffset + 1] = view.getFloat32(offset + 4, true);

    scales[vectorOffset + 2] = view.getFloat32(offset + 8, true);

    offset += 12;
  } else {
    scales[vectorOffset] = halfToFloat(view.getUint16(offset, true));

    scales[vectorOffset + 1] = halfToFloat(view.getUint16(offset + 2, true));

    scales[vectorOffset + 2] = halfToFloat(view.getUint16(offset + 4, true));

    offset += 6;
  }

  let w;
  let x;
  let y;
  let z;

  if (compressionLevel === 0) {
    w = view.getFloat32(offset, true);
    x = view.getFloat32(offset + 4, true);
    y = view.getFloat32(offset + 8, true);
    z = view.getFloat32(offset + 12, true);

    offset += 16;
  } else {
    w = halfToFloat(view.getUint16(offset, true));

    x = halfToFloat(view.getUint16(offset + 2, true));

    y = halfToFloat(view.getUint16(offset + 4, true));

    z = halfToFloat(view.getUint16(offset + 6, true));

    offset += 8;
  }

  /*
   * KSPLAT position coordinates are converted above, but the
   * corresponding quaternion-space conversion has not been verified.
   * Preserve the file's WXYZ convention.
   */
  writeNormalizedQuaternionWXYZ(rotations, outputIndex, w, x, y, z);

  colors[vectorOffset] = view.getUint8(offset) / 255;

  colors[vectorOffset + 1] = view.getUint8(offset + 1) / 255;

  colors[vectorOffset + 2] = view.getUint8(offset + 2) / 255;

  opacities[outputIndex] = view.getUint8(offset + 3) / 255;
}

/**
 * Loads a KSPLAT scene and uniformly samples large files during
 * decoding so memory is allocated only for retained Gaussians.
 */
export function loadKSplat(buffer, { maxGaussians } = {}) {
  if (buffer.byteLength < HEADER_SIZE) {
    throw new Error("Invalid .ksplat file: header is incomplete.");
  }

  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  if (bytes[0] !== 0) {
    throw new Error(`Unsupported .ksplat major version ${bytes[0]}.`);
  }

  const maxSections = view.getUint32(4, true);

  const compressionLevel = view.getUint16(20, true);

  const compression = COMPRESSION[compressionLevel];

  if (!compression) {
    throw new Error(
      `Unsupported .ksplat compression level ${compressionLevel}.`,
    );
  }

  const sections = [];

  let totalCount = 0;
  let dataBase = HEADER_SIZE + maxSections * SECTION_HEADER_SIZE;

  // Section headers describe where each compressed data block begins.
  for (let sectionIndex = 0; sectionIndex < maxSections; sectionIndex++) {
    const header = HEADER_SIZE + sectionIndex * SECTION_HEADER_SIZE;

    const count = view.getUint32(header, true);

    const maxCount = view.getUint32(header + 4, true);

    const bucketSize = view.getUint32(header + 8, true);

    const bucketCount = view.getUint32(header + 12, true);

    const blockSize = view.getFloat32(header + 16, true);

    const bucketStorageSize = view.getUint16(header + 20, true);

    const storedScaleRange = view.getUint32(header + 24, true);

    const scaleRange = storedScaleRange || compression.defaultScaleRange;

    const fullBuckets = view.getUint32(header + 32, true);

    const partialBuckets = view.getUint32(header + 36, true);

    const shDegree = view.getUint16(header + 40, true);

    const shComponents = SH_COMPONENTS[shDegree] ?? 0;

    const bytesPerSplat =
      compression.centerBytes +
      compression.scaleBytes +
      compression.rotationBytes +
      compression.colorBytes +
      shComponents * compression.shBytes;

    const partialMetadataBytes = partialBuckets * 4;

    const bucketStorageBytes =
      bucketStorageSize * bucketCount + partialMetadataBytes;

    sections.push({
      count,
      maxCount,
      bucketSize,
      bucketCount,
      blockSize,
      scaleRange,
      fullBuckets,
      partialBuckets,
      bytesPerSplat,
      metadataOffset: dataBase,
      bucketOffset: dataBase + partialMetadataBytes,
      splatDataOffset: dataBase + bucketStorageBytes,
    });

    totalCount += count;

    dataBase += bytesPerSplat * maxCount + bucketStorageBytes;
  }

  if (!totalCount) {
    throw new Error(".ksplat contains no Gaussians.");
  }

  const sampler = createUniformSampler(totalCount, maxGaussians);

  const data = createGaussianData(sampler.outputCount, {
    sourceCount: totalCount,
    sampled: sampler.sampled,
  });

  const { positions, scales, rotations, colors, opacities } = data;

  debugLog("[KSplatLoader] Decode plan:", {
    sourceCount: totalCount,
    outputCount: data.count,
    sampled: sampler.sampled,
    normalizedBytes:
      positions.byteLength +
      scales.byteLength +
      rotations.byteLength +
      colors.byteLength +
      opacities.byteLength,
  });

  let globalSectionStart = 0;
  let outputIndex = 0;

  for (const section of sections) {
    if (!section.count) {
      continue;
    }

    const globalSectionEnd = globalSectionStart + section.count;

    const bucketIndices =
      compressionLevel === 0 ? null : buildBucketIndices(view, section);

    while (outputIndex < data.count) {
      const globalSourceIndex = sampler.sourceIndexFor(outputIndex);

      if (globalSourceIndex < globalSectionStart) {
        outputIndex++;
        continue;
      }

      if (globalSourceIndex >= globalSectionEnd) {
        break;
      }

      decodeGaussian(
        view,
        section,
        compressionLevel,
        bucketIndices,
        globalSourceIndex - globalSectionStart,
        outputIndex,
        positions,
        scales,
        rotations,
        colors,
        opacities,
      );

      outputIndex++;
    }

    globalSectionStart = globalSectionEnd;

    if (outputIndex >= data.count) {
      break;
    }
  }

  if (outputIndex !== data.count) {
    throw new Error(
      "KSPLAT sampling did not produce the expected number " +
        `of Gaussians (${outputIndex}/${data.count}).`,
    );
  }

  return data;
}

/**
 * Builds a section-local mapping from Gaussian indices to their
 * compression buckets, including partially filled trailing buckets.
 */
function buildBucketIndices(view, section) {
  const result = new Uint32Array(section.count);

  const fullCount = Math.min(
    section.fullBuckets * section.bucketSize,
    section.count,
  );

  for (let i = 0; i < fullCount; i++) {
    result[i] = Math.floor(i / section.bucketSize);
  }

  let output = fullCount;

  for (
    let partial = 0;
    partial < section.partialBuckets && output < section.count;
    partial++
  ) {
    const length = view.getUint32(section.metadataOffset + partial * 4, true);

    const bucketIndex = section.fullBuckets + partial;

    for (let i = 0; i < length && output < section.count; i++) {
      result[output++] = bucketIndex;
    }
  }

  return result;
}
